import React from 'react';
import { X, type LucideIcon } from 'lucide-react';

import { badgeVariants } from './ui/badge';
import { cn } from '../lib/utils';

interface ChipBaseProps {
  children: React.ReactNode;
  /** Monospace text, for machine values (a slug, a provider key). Never for labels. */
  mono?: boolean;
  /**
   * 'muted' (default) for a member of a set (a role, an identifier); 'warning'
   * only for elevated privilege (system administrator access).
   */
  variant?: 'muted' | 'warning';
  icon?: LucideIcon;
  /** A secondary value on hover (a lens alias, an event type slug). */
  title?: string;
  /** A short marker inside the pill, after the name; never truncated. */
  flag?: React.ReactNode;
  className?: string;
}

/** `removeLabel` is required whenever `onRemove` is set: the X has no text of its own. */
type ChipRemoveProps =
  | { onRemove?: undefined; removeLabel?: undefined }
  | { onRemove: () => void; removeLabel: string };

export type ChipProps = ChipBaseProps & ChipRemoveProps;

/**
 * One identifier shown as a member of a set — a role name (RoleChips), an event
 * or sensor type, a lens name, an identity provider. Fully rounded, 12px, muted.
 *
 * Not a status: runtime state goes through StatusBadge. Not a count: a count of
 * work waiting is a CountBadge, and a quantity shown as data is plain
 * tabular-nums text.
 *
 * `onRemove` + `removeLabel` draw the chip's own remove control, for a set the
 * person edits in place (the Request Builder's selected event types). It is the
 * one embedded control smaller than Button `icon-xs`: a 16px glyph box with a
 * 24px hit area, because a 28px button would stretch every chip row. RoleChips
 * never passes it — roles change through the Manage roles checklist.
 */
export const Chip: React.FC<ChipProps> = ({
  children,
  mono = false,
  variant = 'muted',
  icon: Icon,
  title,
  flag,
  onRemove,
  removeLabel,
  className,
}) => (
  <span
    className={cn(badgeVariants({ variant }), 'max-w-full', mono && 'font-mono', onRemove && 'pr-1', className)}
    title={title}
    data-chip=""
  >
    {Icon ? <Icon className="h-3 w-3 shrink-0" aria-hidden="true" /> : null}
    <span className="truncate">{children}</span>
    {flag != null ? <span className="shrink-0">{flag}</span> : null}
    {onRemove ? (
      <button
        type="button"
        onClick={onRemove}
        aria-label={removeLabel}
        className={cn(
          'relative -mr-0.5 ml-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full text-muted-foreground',
          "after:absolute after:-inset-1 after:content-['']",
          'hover:bg-foreground/10 hover:text-foreground',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
        )}
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
    ) : null}
  </span>
);
